<template>
  <div>
    <SelectorPanel
      type="assets"
      text="Assets"
      :force-full-screen="true"
      :disabled="disabled"
      :force-close="forceClose"
      :url-path="urlPath"
    >
      <SelectorPanelHeader
        type="assets"
        :title="labelTitle"
        class="pb-2"
        dense
        @click-close="handleClickClose"
      ></SelectorPanelHeader>

      <AssetsContent @click-close="handleClickClose"></AssetsContent>
    </SelectorPanel>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashIsEqual from "lodash/isEqual";

import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_ASSETS,
  MODULE_PREFIX_USER,
} from "@/store";
import {
  GET_PREFERENCES,
  GET_DETAILS_SIDEBAR,
  GET_ASSETS_REFRESH_MAP,
} from "@/store/actions.type";

import { MAP_TILE_SERVER_DYNAMIC } from "@/common/config";
import { LABELS } from "@/common/constants/labels";
import { addTileData, removeTileData } from "@/common/maps/features";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { SUPPORT_EMAIL } from "@/common/constants/misc";

export const ASSETS_ROUTE = "/assets";

export default {
  name: "AssetsSelector",
  components: {
    SelectorPanel: () => import("@/components/common/SelectorPanel.vue"),
    SelectorPanelHeader: () =>
      import("@/components/common/SelectorPanelHeader.vue"),
    AssetsContent: () =>
      import("@/components/assetManagement/AssetsContent.vue"),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    urlPath: ASSETS_ROUTE,
    forceClose: null,
    labelTitle: LABELS.assets,
    layerId: "layer-assets",
    baseTileUrl: `${MAP_TILE_SERVER_DYNAMIC}assets/{z}/{x}/{y}.pbf`,
    options: {
      enabled: undefined,
      filterTitle: undefined,
      filterCountries: undefined,
      filterAssetTypeIds: undefined,
      filterRatings: undefined,
    },
  }),
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      refreshDataMap: MODULE_PREFIX_MAP + "refreshData",
      mainMapHijacker: MODULE_PREFIX_MAP + "mainMapHijacker",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      refreshMap: MODULE_PREFIX_ASSETS + "refreshMap",
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
  },
  mounted() {
    this.getPrefs();
  },
  methods: {
    getPrefs() {
      this.$store.dispatch(MODULE_PREFIX_USER + GET_PREFERENCES);
    },
    handleClickClose() {
      this.forceClose = new Date().toISOString();
    },
    buildTileUrl() {
      const searchParams = new URLSearchParams();
      // Additional filters for assets
      if (
        !!this.options.filterCountries &&
        this.options.filterCountries.length > 0
      ) {
        searchParams.append("countries", `${this.options.filterCountries}`);
      }
      if (!!this.options.filterTitle) {
        searchParams.append("title", `%${this.options.filterTitle}%`);
      }
      if (
        !!this.options.filterAssetTypeIds &&
        this.options.filterAssetTypeIds.length > 0
      ) {
        searchParams.append("types", `${this.options.filterAssetTypeIds}`);
      }
      let url =
        searchParams.size > 0
          ? `${this.baseTileUrl}?${searchParams.toString()}`
          : this.baseTileUrl;

      return url;
    },
    getFilter() {
      if (
        !this.options.filterRatings ||
        Object.keys(this.options.filterRatings).length === 0
      ) {
        return undefined;
      }
      const ratingFilters = Object.entries(this.options.filterRatings).map(
        ([key, value]) => {
          return ["in", ["get", key], ["literal", value]];
        }
      );
      return ["all", ...ratingFilters];
    },
    addToMap() {
      this.removeFromMap();
      const tileUrl = this.buildTileUrl();
      const layerFilter = this.getFilter();
      // draw circle
      let layerOptions = {
        id: `${this.layerId}-background`,
        source: `${this.layerId}-background`,
        type: "circle",
        paint: {
          "circle-radius": 9,
          "circle-color": ["get", "color"],
          "circle-stroke-width": 2,
          "circle-stroke-color": ["get", "color"],
        },
        ...(layerFilter && { filter: layerFilter }),
      };

      layerOptions["source-layer"] = "assets";
      layerOptions["metadata"] = "seerist-points-assets";

      let source = {
        type: "vector",
        tiles: [tileUrl],
        tolerance: 0,
      };

      let options = {
        id: `${this.layerId}-background`,
        source: source,
        layerOptions: [layerOptions],
        map: this.map,
        mapClickEvent: this.mapClickEvent,
      };

      addTileData(options);

      // draw icon
      layerOptions = {
        id: this.layerId,
        source: this.layerId,
        type: "symbol",
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-size": 0.55,
          "icon-image": ["get", "icon"],
        },
        paint: {
          "icon-color": "#ffffff",
        },
        ...(layerFilter && { filter: layerFilter }),
      };

      layerOptions["source-layer"] = "assets";
      layerOptions["metadata"] = "seerist-points-assets";

      source = {
        type: "vector",
        tiles: [tileUrl],
        tolerance: 0,
      };

      options = {
        id: this.layerId,
        source: source,
        layerOptions: [layerOptions],
        map: this.map,
        mapClickEvent: this.mapClickEvent,
      };

      addTileData(options);
    },
    async mapClickEvent(e, features) {
      e.originalEvent.stopPropagation();

      if (features.length > 0) {
        this.detailsSidebar.features = features;
        this.detailsSidebar.visible = true;
        this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.assets;
        this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.assets;

        await this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
          this.detailsSidebar
        );

        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.assets,
          EVENT_ACTIONS.clicked,
          `Asset Map Click --- ${features[0].properties.gid}`
        );
      }
    },
    removeFromMap() {
      if (this.map) {
        removeTileData({
          map: this.map,
          layerId: `${this.layerId}-background`,
          sourceId: `${this.layerId}-background`,
        });
        removeTileData({
          map: this.map,
          layerId: this.layerId,
          sourceId: this.layerId,
        });
      }
    },
    handleMapRefresh(newVal) {
      if (
        (lodashGet(this.preferences, "assets.enabled") ||
          this.options.enabled) &&
        !newVal
      ) {
        this.addToMap();
      } else {
        this.removeFromMap();
      }
    },
  },
  watch: {
    refreshMap: {
      handler(event) {
        if (lodashIsEqual(event, this.options)) {
          return;
        }

        this.options = {
          ...event,
        };

        if (
          lodashGet(this.preferences, "assets.enabled") ||
          this.options.enabled
        ) {
          this.addToMap();
        } else {
          this.removeFromMap();
        }
      },
    },
    mainMapHijacker: {
      handler(newVal) {
        this.handleMapRefresh(newVal);
      },
    },
    refreshDataMap: {
      handler(newVal) {
        this.handleMapRefresh(newVal);
      },
      deep: true,
    },
  },
};
</script>

<style></style>

<style scoped></style>
